import { useState, useEffect } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { TrendingUp, ChevronDown, ChevronUp, Target } from "lucide-react";
import {
  ProgressionType,
  PROGRESSION_TYPE_OPTIONS,
  calculateProgressionPreview,
} from "@/hooks/use-habits";
import { cn } from "@/lib/utils";

interface ProgressionConfigProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  type: ProgressionType;
  onTypeChange: (type: ProgressionType) => void;
  initialTarget: number;
  onInitialTargetChange: (value: number) => void;
  increment: number;
  onIncrementChange: (value: number) => void;
  maxTarget: number | undefined;
  onMaxTargetChange: (value: number | undefined) => void;
  unit: string | null;
  trackingType: string;
}

export function ProgressionConfig({
  enabled,
  onEnabledChange,
  type,
  onTypeChange,
  initialTarget,
  onInitialTargetChange,
  increment,
  onIncrementChange,
  maxTarget,
  onMaxTargetChange,
  unit,
  trackingType,
}: ProgressionConfigProps) {
  const [showPreview, setShowPreview] = useState(false);

  // Calculate preview data
  const preview = enabled && initialTarget > 0 && increment > 0
    ? calculateProgressionPreview(initialTarget, increment, maxTarget, type, 8)
    : [];

  // Don't show for boolean habits
  if (trackingType === "boolean") {
    return null;
  }

  return (
    <div className="space-y-4 pt-2 border-t">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="progression-enabled" className="font-medium">
            Progressive Target
          </Label>
        </div>
        <Switch
          id="progression-enabled"
          checked={enabled}
          onCheckedChange={onEnabledChange}
        />
      </div>

      {enabled && (
        <div className="space-y-4 pl-6 animate-in slide-in-from-top-2">
          <p className="text-sm text-muted-foreground">
            Automatically increase the target over time to build up the habit gradually.
          </p>

          {/* Initial Target */}
          <div className="space-y-2">
            <Label htmlFor="initial-target">
              Starting Target{" "}
              {unit && (
                <span className="text-muted-foreground font-normal">
                  ({unit})
                </span>
              )}
            </Label>
            <Input
              id="initial-target"
              type="number"
              value={initialTarget || ""}
              onChange={(e) => onInitialTargetChange(Number(e.target.value))}
              placeholder="e.g., 5"
              min={trackingType === "quality_score" ? 1 : 0}
              max={trackingType === "quality_score" ? 10 : undefined}
            />
          </div>

          {/* Progression Type */}
          <div className="space-y-2">
            <Label>Increase Frequency</Label>
            <Select value={type} onValueChange={(v) => onTypeChange(v as ProgressionType)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PROGRESSION_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <span className="flex flex-col items-start">
                      <span>{option.label}</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Increment Amount */}
          <div className="space-y-2">
            <Label htmlFor="increment">
              Increase By{" "}
              {unit && (
                <span className="text-muted-foreground font-normal">
                  ({unit} per period)
                </span>
              )}
            </Label>
            <Input
              id="increment"
              type="number"
              value={increment || ""}
              onChange={(e) => onIncrementChange(Number(e.target.value))}
              placeholder="e.g., 5"
              min={0}
            />
          </div>

          {/* Max Target (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="max-target">
              Maximum Target{" "}
              <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input
              id="max-target"
              type="number"
              value={maxTarget ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                onMaxTargetChange(val === "" ? undefined : Number(val));
              }}
              placeholder="e.g., 30"
              min={initialTarget || 0}
              max={trackingType === "quality_score" ? 10 : undefined}
            />
            <p className="text-xs text-muted-foreground">
              Target will stop increasing after reaching this value
            </p>
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <Collapsible open={showPreview} onOpenChange={setShowPreview}>
              <CollapsibleTrigger className="flex items-center gap-2 text-sm text-primary hover:underline">
                {showPreview ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
                {showPreview ? "Hide" : "Show"} progression preview
              </CollapsibleTrigger>
              <CollapsibleContent className="pt-3">
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-3">
                    <Target className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm font-medium">Target Progression</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {preview.map((point, idx) => (
                      <Badge
                        key={point.period}
                        variant={idx === 0 ? "default" : "secondary"}
                        className={cn(
                          "text-xs",
                          maxTarget !== undefined && point.target >= maxTarget && "bg-success/15 text-success"
                        )}
                      >
                        {point.label}: {point.target} {unit || ""}
                      </Badge>
                    ))}
                  </div>
                  {maxTarget !== undefined && preview[preview.length - 1]?.target >= maxTarget && (
                    <p className="text-xs text-success mt-2">
                      Maximum target reached
                    </p>
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        </div>
      )}
    </div>
  );
}
